import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

// import { EquiposUtilizadosOTTableType } from "@/erp/tecnico/orden-trabajo/shared/components/SaveOrdenTrabajo/equipos";
import {
  EstadosActivacionInstallOTEnumChoice,
  ROUTER_PATHS,
  activacionInstallOtSchema,
  getMissingRequiredSalidaPhotos,
  gridSizeMdLg12,
  hasPermission,
  texto,
  useRecordDirectLock,
} from "@/shared";
import {
  ActivateInstallOTData,
  OrdenTrabajoQueryKeysEnum,
  useFetchNaps,
  useFetchUbicacionProductosEnabled,
  useUpdateContrato,
  // useUpdateNaps,
  useUpdateOrdenTrabajo,
} from "@/shared/actions/erp";
import {
  SingleFormBoxScene,
} from "@/shared/components/ui";
import { AlertSeverityEnum, Contrato, EstadoPuertoNap, EstadosNavegacionEnum, ONTOption, OrdenTrabajo } from "@/shared/interfaces";
import { useEquiposAdicionales } from "@/store/erp";
import { 
  // useUiConfirmModalStore, 
  useUiStore } from "@/store/ui";
import { useEquiposFlotaActivacionInstall } from "../hooks";
import {
  ClientesFormComponentActivaciones,
  DatosAdicionalesFormComponentActivaciones,
  DatosTecnicosFormComponentActivaciones,
} from "./form";
import SummaryPanelComponentActivaciones from "./form/SummaryPanelComponentActivaciones";
import { CustomAlertCard } from "@/shared/components/ui/Alerts";
import { CategoryProducCodeEnum } from "@/shared/interfaces/erp/inventario";
import useSaveActivacion from "../hooks/useSaveActivacion";
import { toast } from "react-toastify";
import { queryClient } from "@/shared/query-client/query-client";
import { PermissionsEnum } from "@/auth/interfaces";
import { OntParaFormulario } from "@/erp/operaciones/activaciones/traslados/shared/components/ont-cliente/ont-cliente.actions";
import {
  esOrdenTraslado,
  fotosProcesoFaltantes,
  resumenFotosTraslado,
} from "@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/fotos-traslado.utils";
import { useOrdenTrabajoFotosLive } from "@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/fotos-traslado.actions";
import { useExcedenteFibraLive } from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/excedente-fibra.actions";
import { excedenteFibraPendiente } from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/excedente-fibra.utils";
import {
  CobroOntForm,
  cobroGuardadoDe,
  guardarCobroOnt,
  quitarCobroOnt,
} from "@/erp/operaciones/activaciones/traslados/shared/components/ont-cliente/ont-cobro.actions";

const returnUrlActivacionInstallOTPage =
  ROUTER_PATHS.activaciones.activacionesInstalacionNav;

export type SaveActivacionInstallPendingOTProps = {
  // titlePageNode?: React.ReactNode;
  titlePage?: string;
  ordenTrabajo: OrdenTrabajo;

  customReturnUrl?: string;
  disableSubmitBtn?: boolean;
  /**
   * Secciones adicionales que se renderizan DENTRO del formulario, después de
   * "DATOS ADICIONALES" y antes del panel resumen. Las usan los traslados
   * (evidencia fotográfica + otros conceptos); instalaciones no las pasa, así
   * que su formulario no cambia.
   */
  seccionesAdicionales?: React.ReactNode;
  /**
   * Solo TRASLADOS: deja elegir la ONT que el cliente ya tiene instalada, que
   * no esta en el stock del tecnico y por eso no aparece en el combo.
   * Instalaciones no pasa esta prop, asi que su formulario no cambia.
   */
  permitirOntDelCliente?: boolean;
};

const SaveActivacionInstallPendingOT: React.FC<
  SaveActivacionInstallPendingOTProps
> = ({
  ordenTrabajo,
  titlePage,
  customReturnUrl = returnUrlActivacionInstallOTPage,
  // titlePageNode = null,
  disableSubmitBtn = false,
  seccionesAdicionales = null,
  permitirOntDelCliente = false,
}) => {
  ///* hooks ------------------------
  const navigate = useNavigate();
  const [optionsONT, setOptionsONT] = useState<ONTOption[]>([]);
  /**
   * ONTs del cliente (traslados) en un estado APARTE, no dentro de
   * `optionsONT`: el efecto que llena ese arreglo lo reescribe completo con el
   * stock del tecnico cada vez que corre, asi que una opcion agregada ahi se
   * perderia. Se concatenan solo al pasarlas al formulario.
   */
  const [ontsDelCliente, setOntsDelCliente] = useState<ONTOption[]>([]);
  /**
   * Check "Usar ONT cliente" y cobro de la ONT entregada (traslados).
   * El cobro se precarga con lo que ya tenga guardada la orden, para que al
   * volver a entrar se vea lo que se definio antes.
   */
  const [usarOntCliente, setUsarOntCliente] = useState(false);
  /**
   * Apertura del buscador, SEPARADA del check. El check dice de donde sale la
   * ONT (del cliente o del stock) y queda marcado despues de elegir; el
   * dialogo es solo la ventana y se cierra al confirmar. Atarlos era lo que
   * dejaba el modal abierto tras "Usar esta ONT".
   */
  const [dialogoOntClienteAbierto, setDialogoOntClienteAbierto] = useState(false);
  const [cobroOnt, setCobroOnt] = useState<CobroOntForm | null>(() => {
    const guardado = cobroGuardadoDe(ordenTrabajo);
    return guardado
      ? {
          tipo_pago: guardado.tipo_pago,
          meses_pago: guardado.meses_pago ?? null,
          valor_sin_iva: Number(guardado.valor_sin_iva) || 0,
        }
      : null;
  });
  const [canChangeNap, setCanChangeNap] = useState(false);
  // TRASLADO: reutiliza este formulario; el cliente ya está ACTIVO, así que se
  // le permite cambiar NAP/Puerto/Router y se exige NAP + puerto libre al
  // gestionar. Instalaciones (false) no cambian.
  const esTraslado = esOrdenTraslado(ordenTrabajo);
  // Fotos del técnico EN VIVO (solo traslados): la validación de Guardar y
  // Finalizar las ve al día aunque el técnico las acabe de subir.
  const { ot: otFotosTraslado } = useOrdenTrabajoFotosLive(ordenTrabajo, esTraslado);
  // "Fibra utilizada" registrada por el técnico (vive en
  // agendamiento.observaciones.excedente_fibra; la escribe solo el backend).
  const { excedente: excedenteFibraTraslado } = useExcedenteFibraLive(ordenTrabajo, esTraslado);
  const { releaseLock } = useRecordDirectLock(ordenTrabajo?.id_orden_trabajo?.toString() || "")
  // const { handleMethodChange, valueTab } = useTabsFormOnly();

  useEquiposFlotaActivacionInstall({ ordenTrabajo });

  ///* global state ------------------------
  // const equipoSerieUtilizadaActivacion: EquiposUtilizadosOTTableType[] =
  //   useMaterialesUtilizadosStore((s) => s.equipoSerieUtilizadaActivacion);
  const setAlert = useUiStore((s) => s.setAlert);
  const productos = useEquiposAdicionales(s => s.productos)
  const setProductosAdicionales = useEquiposAdicionales(s => s.setProductos)

  ///* form ------------------------
  const form = useForm<any>({
    resolver: yupResolver(activacionInstallOtSchema) as any,
    defaultValues: {
      actualizar_nodo_olt: false,
      ont_a_utilizar: ordenTrabajo?.equipos_utilizados?.[0] || null,
      rawNap: ordenTrabajo?.nap_data ?? null,
      router: ordenTrabajo?.router ?? null,
      puntos_red_no_instalados: 0,
      puntos_red_instalados: 0,
      routers_adicionales_instalados: 0,
      nap: ordenTrabajo?.nap ?? "",
      es_borrador: true,
      tipo_instalacion: ordenTrabajo?.tipo_instalacion||"",
      subtipo_instalacion: ordenTrabajo?.subtipo_instalacion||null,
      ssid: ordenTrabajo?.contrato_data?.ssid || "",
      password_wifi: ordenTrabajo?.contrato_data?.password_wifi || "",
    },
  })
  const esBrasValue = form.watch("es_bras");

  const [lat, lng] = useMemo(() => {
    const coords = (ordenTrabajo?.coordenadas || '').split(',');
    const latValue = coords?.[0]?.trim() || undefined;
    const lngValue = coords?.[1]?.trim() || undefined;
    return [latValue, lngValue] as const;
  }, [ordenTrabajo?.coordenadas]);

  ///* hooks ------------------------
  const {
    cambioDeNap,
    cambioPuertoDeNap
  } = useSaveActivacion({ordenTrabajo, form})


  ///* mutations ------------------------
  const setActivationDataNoReturn = useUpdateOrdenTrabajo<Partial<ActivateInstallOTData>>({
    enableRedirect: false,
  });
  const setActivationData = useUpdateOrdenTrabajo<Partial<ActivateInstallOTData>>({
    navigate,
    returnUrl: customReturnUrl,
  });
  const setContratoDataNoReturn = useUpdateContrato<Partial<Contrato>>({
    enableRedirect:false,
  });
  const {
    isLoading: isLoadingNapsByCity,
    data: napsByCity,
  } = useFetchNaps({
    lat,
    lng,
    page_size: 1000,
    estado:true
    // ciudad: filterCity,
    // lat: currentCoordenadas.split(',')[0],
    // lng: currentCoordenadas.split(',')[1],

    // ...(selectedNode && { nodo_codigo: selectedNode! }),

  });

    ///* fetch ------------------------
  const ubicacionProductosParams = useMemo(() => {
    return {
      page_size: 1000,
      page: 1,
      categoria_producto: CategoryProducCodeEnum.EQUIPOS,
      ubicacion: ordenTrabajo?.flota_data?.ubicacion,
      es_ont: true,
      // filterByState: false
    };
  }, [ordenTrabajo?.flota_data?.ubicacion]);

  const { data: dataUbicacionProd,
    isLoading: isloadingUbProducto,
    // isRefetching: isRefetchingUbProducto
  } = useFetchUbicacionProductosEnabled({
    enabled: !!ordenTrabajo?.flota_data?.ubicacion,
    params: ubicacionProductosParams
  });

  ///* handlers ------------------------
  /**
   * ONT del cliente (traslados). La opcion no viene de `ubicacion-producto`
   * —ese endpoint solo trae el stock del tecnico—, asi que se agrega a mano a
   * la lista del combo y se selecciona, con el mismo efecto que tiene elegirla
   * ahi: tambien queda como unico equipo utilizado.
   */
  const onOntClienteSeleccionada = (ont: OntParaFormulario) => {
    setOntsDelCliente((previas) => {
      const yaEsta = previas.some(
        (o: any) => o?.series?.[0] === ont.series[0]
      );
      return yaEsta ? previas : [ont as any, ...previas];
    });
    form.reset({
      ...form.getValues(),
      ont_a_utilizar: ont,
      equipos_utilizados: [ont],
    });
  };

  /**
   * Cambia el origen de la ONT. Al desmarcar, si la ONT elegida venia del
   * cliente se limpia la seleccion: el operador tiene que elegir una del stock
   * (y ahi recien aparece el cobro).
   */
  const onCambiarUsarOntCliente = (valor: boolean) => {
    setUsarOntCliente(valor);
    setDialogoOntClienteAbierto(valor);
    if (valor) {
      // La del cliente no se cobra: se descarta lo que hubiera cargado.
      setCobroOnt(null);
      return;
    }
    const actual = form.getValues()?.ont_a_utilizar;
    const esDelCliente = ontsDelCliente.some(
      (o: any) => o?.series?.[0] && o.series[0] === actual?.series?.[0]
    );
    if (esDelCliente) {
      form.reset({ ...form.getValues(), ont_a_utilizar: null, equipos_utilizados: [] });
    }
  };

  /**
   * Cierra el buscador. Si el operador cancelo sin elegir, el check vuelve
   * atras; si eligio, el check queda marcado y la ONT seleccionada.
   */
  const onCerrarDialogoOntCliente = (huboSeleccion: boolean) => {
    setDialogoOntClienteAbierto(false);
    if (!huboSeleccion) setUsarOntCliente(false);
  };

  /**
   * Guarda (o quita) el cobro de la ONT entregada. Va por su propio endpoint
   * porque se escribe en una clave de las observaciones que el PUT de
   * agendamiento no puede pisar.
   */
  const guardarCobroDeLaOnt = async (data: any) => {
    if (!permitirOntDelCliente) return;
    const id = ordenTrabajo?.id_orden_trabajo;
    if (!id) return;
    const ont = data?.equipos_utilizados?.[0] || data?.ont_a_utilizar;
    const serie = ont?.series?.[0];
    if (usarOntCliente || !ont || !serie || !cobroOnt?.tipo_pago) {
      // Sin ONT del stock o sin metodo elegido no hay nada que cobrar; si
      // habia un cobro de antes, se quita.
      await quitarCobroOnt(id);
      return;
    }
    await guardarCobroOnt(id, {
      serie,
      codigo: ont.codigo,
      descripcion: ont.name,
      tipo_pago: cobroOnt.tipo_pago,
      meses_pago: cobroOnt.meses_pago ?? null,
      valor_sin_iva: Number(cobroOnt.valor_sin_iva) || 0,
    });
  };

  const onSave = async (data: any, withReturnUrl?:boolean) => {
    if(!!withReturnUrl) {
      form.setValue("es_borrador",false)
      data.es_borrador = false
    }

    // TRASLADO: no se puede Guardar y Finalizar sin la evidencia del técnico:
    // las 2 fotos de proceso (punto inicial y final) y sin fotos eliminadas
    // pendientes de reponer. Las de salida van después, cuando el técnico
    // finaliza con materiales. El borrador no se frena.
    if (withReturnUrl && esTraslado) {
      const resumenFotos = resumenFotosTraslado(otFotosTraslado);
      const faltanProceso = fotosProcesoFaltantes(otFotosTraslado);
      if (faltanProceso.length > 0) {
        setAlert(
          true,
          `El técnico aún no sube las fotos de proceso del traslado (${faltanProceso.join(" y ")}). Debe subirlas antes de Guardar y Finalizar la activación.`,
          AlertSeverityEnum.error
        );
        return;
      }
      if (resumenFotos.faltanIngreso > 0) {
        setAlert(
          true,
          `Hay ${resumenFotos.faltanIngreso === 1 ? "1 foto" : `${resumenFotos.faltanIngreso} fotos`} de ingreso eliminada${resumenFotos.faltanIngreso === 1 ? "" : "s"} que el técnico debe volver a subir antes de Guardar y Finalizar.`,
          AlertSeverityEnum.error
        );
        return;
      }
      // Fibra utilizada: mismo trato que las fotos de proceso. El técnico la
      // registra (mínimo 1 metro) en su pestaña MATERIALES; si supera los
      // metros gratis queda PENDIENTE y Activaciones debe aprobarla (precio y
      // forma de pago, cuadro Excedente de fibra) antes de Guardar y Finalizar.
      const metrosFibraRegistrados = Number(excedenteFibraTraslado?.metros ?? 0);
      if (!excedenteFibraTraslado || !(metrosFibraRegistrados >= 1)) {
        setAlert(
          true,
          "El técnico aún no registra la Fibra utilizada del traslado (mínimo 1 metro, cuadro Fibra utilizada de su pestaña MATERIALES). Debe guardarla antes de Guardar y Finalizar la activación.",
          AlertSeverityEnum.error
        );
        return;
      }
      if (excedenteFibraPendiente(excedenteFibraTraslado)) {
        setAlert(
          true,
          "El excedente de fibra del traslado está PENDIENTE de aprobación. Apruébalo (precio y forma de pago, cuadro Excedente de fibra) antes de Guardar y Finalizar.",
          AlertSeverityEnum.error
        );
        return;
      }
    }

    // Traslados: si la ONT no es la del cliente, hay que decir como se cobra.
    // Para los casos sin cargo esta COMODATO en el catalogo, asi que siempre
    // hay una opcion valida. Solo frena el guardado final, no el borrador.
    if (withReturnUrl && permitirOntDelCliente && !usarOntCliente) {
      const ontSel = data?.equipos_utilizados?.[0] || data?.ont_a_utilizar;
      if (ontSel?.series?.[0] && !cobroOnt?.tipo_pago) {
        setAlert(
          true,
          "Elija el método de pago de la ONT entregada al cliente.",
          AlertSeverityEnum.error
        );
        return;
      }
    }

    // El cobro de la ONT va primero: si falla, no se guarda nada a medias.
    try {
      await guardarCobroDeLaOnt(data);
    } catch (error: any) {
      setAlert(
        true,
        error?.response?.data?.message || error?.message ||
          "No se pudo guardar el cobro de la ONT.",
        AlertSeverityEnum.error
      );
      return;
    }
    // const proccesedEquiposUtilizados = equipos_utilizados.map((x)=>{
    //   if(x?.producto_data?.es_ont || x?.producto_data?.descripcion?.includes("ONT")){
    //     return {
    //       "id": x.id,
    //       "codigo": x.codigo,
    //       "series": x.selected_series || x.pre_selected_series,
    //       "cantidad": x.cantidad.toString(),
    //       "stock_actual": x.cantidad,
    //       "producto_data": x.producto_data,
    //       "contiene_serie": x.contiene_serie,
    //     }
    //   }
    //   return x
    // })

    // data.fecha_hora_inicio_instalacion = dayjs(data.fecha_hora_inicio_instalacion).format("HH:mm:ss");
    // data.fecha_hora_fin_instalacion = dayjs(data.fecha_hora_fin_instalacion).add(1, "hour").format("HH:mm:ss");

    // if (!data?.fecha_hora_inicio_instalacion && !data?.fecha_hora_fin_instalacion)
    //   return setAlert(
    //     true,
    //     "Debe ingresar al menos la hora de inicio de la instalación.",
    //     AlertSeverityEnum.error
    //   );

    // =========================================================================
    // ESTE PROCESO AHORA LO REALIZA EL TÉCNICO
    // =========================================================================
    ///* validate serie ONT -------------
    // let thereIsSerieONT = false;
    // let selectedSerie = "";
    // for (const item of equipoSerieUtilizadaActivacion) {
    //   if (item?.selected_series?.length) {
    //     thereIsSerieONT = true; 
    //     selectedSerie = item.selected_series[0];
    //     break;
    //   }
    // }
    // if (!thereIsSerieONT)
    //   return setAlert(
    //     true,
    //     "Debe ingresar una serie ONT.",
    //     AlertSeverityEnum.error
    //   );


    // data.forma_pago_adicionales_tecnico = {
    //   forma_pago: form.getValues()?.forma_pago_aux || "",
    //   entidad_financiera: form.getValues()?.entidad_financiera_aux || "",
    //   cooperativa: form.getValues()?.cooperativa_aux || "",
    //   codigo_forma_pago: form.getValues()?.codigo_forma_pago_aux || "",
    //   tipo_contrato: form.getValues()?.tipo_contrato_aux || "",
    //   tarjeta: form.getValues()?.tarjeta_aux || "",
    //   valor_instalacion_total: form.getValues()?.valor_instalacion_total_aux || 0,
    //   valor_instalacion: form.getValues()?.valor_instalacion_aux || 0,
    //   valor_instalacion_iva: form.getValues()?.valor_instalacion_iva_aux || 0,
    //   numero_cuenta: form.getValues()?.numero_cuenta_aux || "",
    //   tipo_cuenta_bancaria: form.getValues()?.tipo_cuenta_bancaria_aux || "",
    //   numero_tarjeta: form.getValues()?.numero_tarjeta_aux || ""
    // }
    // =========================================================================
    // TRASLADO: validaciones de NAP/puerto (mismas reglas que instalaciones:
    // NAP y puerto obligatorios al gestionar, y el puerto no puede estar
    // asignado a otro cliente; en instalaciones el icono "Puerto Asignado a
    // Cliente" lo muestra, aquí además se bloquea).
    // =========================================================================
    if (esTraslado && !!withReturnUrl) {
      if (!data?.nap || !data?.puerto) {
        return setAlert(
          true,
          "Selecciona la NAP y el puerto nuevos del traslado antes de gestionar.",
          AlertSeverityEnum.error,
        );
      }
      const puertosNap: any[] = form.getValues()?.rawNap?.puertos || ordenTrabajo?.nap_data?.puertos || [];
      const puertoSel = puertosNap.find((p: any) => `${p?.puerto}` === `${data?.puerto}`);
      const idContrato = ordenTrabajo?.contrato_data?.id_contrato;
      const ocupadoPorOtro =
        !!puertoSel &&
        [EstadoPuertoNap.OCUPADO, EstadoPuertoNap.EN_USO].includes(puertoSel?.estado) &&
        !!puertoSel?.id_cliente_contrato &&
        `${puertoSel.id_cliente_contrato}` !== `${idContrato}`;
      if (ocupadoPorOtro) {
        return setAlert(
          true,
          `El puerto ${data.puerto} de la NAP seleccionada está asignado a otro cliente. Elige un puerto disponible.`,
          AlertSeverityEnum.error,
        );
      }
    }

    // =========================================================================
    // Se cambia estado de navegación de contrato
    // =========================================================================
    const estadoNavegacion = ordenTrabajo?.contrato_data?.estado_navegacion===EstadosNavegacionEnum.ACTIVO
    const finalizaActivacion = !!withReturnUrl && !data?.es_borrador;

    if(finalizaActivacion){
      const fotosFaltantes = getMissingRequiredSalidaPhotos(ordenTrabajo, data);
      if(fotosFaltantes.length > 0){
        return setAlert(
          true,
          "No es posible continuar con el proceso de activación, debido a que el instalador aún tiene pendiente la carga de las fotografías obligatorias. Una vez completada esta actividad, se podrá dar continuidad al proceso.",
          AlertSeverityEnum.warning,
          7000,
        );
      }
    }

    try {
      // =========================================================================
      // Si se cambia solamente el puerto de la nap
      // =========================================================================
      const habilitadoCambioPuerto = data?.puerto 
        && data?.puerto != ordenTrabajo?.puerto
        && data?.nap == ordenTrabajo?.nap
        && ordenTrabajo?.nap_data?.puertos
      if(habilitadoCambioPuerto){
        await cambioPuertoDeNap()
      }

      // =========================================================================
      // Si se cambia la nap que se utilizará
      // =========================================================================
      const habilitadoCambioNap = data?.nap 
        && data?.nap != ordenTrabajo?.nap
        && ordenTrabajo?.nap_data?.puertos
      if(habilitadoCambioNap){
        await cambioDeNap()
      }
      
      await setContratoDataNoReturn.mutateAsync({
        id: ordenTrabajo?.contrato_data?.id_contrato!,
        data: {
          puerto: data?.puerto,
          olt: data?.olt,
          nodo: data?.nodo,
          nap: data?.nap,
          router: data?.router,
          ipv4_actual: data?.ipv4_actual,
          ipv4_anterior: data?.ipv4_anterior,
          pppoe: data?.pppoe,
          pppassword: data?.pppassword,
          es_bras: data?.es_bras,
          bras: data?.bras
        }
      })
      if(withReturnUrl){
        await setActivationData.mutateAsync({
          id: ordenTrabajo?.id_orden_trabajo!,
          data: {
            actualizar_nodo_olt: data?.actualizar_nodo_olt,
            ipv4_actual: data?.ipv4_actual,
            // ipv6_actual: data.ipv6_actual!,
            pppoe: data?.pppoe,
            pppassword: data?.pppassword,

            // ----
            nodo: data?.nodo,
            olt: data?.olt,
            nap: data?.nap,
            puerto: data?.puerto,
            // serie_ont: selectedSerie,
            observaciones: {
              ...ordenTrabajo?.observaciones,
              observacion_activacion: data?.observacion_activacion ||"",
            },

            estado_activacion: !!data?.fecha_hora_inicio_instalacion 
            && data?.estado_navegacion === EstadosNavegacionEnum.ACTIVO
            && !data?.es_borrador? // ACTIVAR ESTO PARA CUANDO SE TENGA LO DE ROUTERS estadoNavegacion ? 
            EstadosActivacionInstallOTEnumChoice.GESTIONADA : undefined,
            // TRASLADO: esta fecha es el semáforo (verde solo si la activación
            // TERMINÓ bien) y la escribe el propio flujo de activación; el borrador
            // no la toca. En instalaciones se mantiene el comportamiento de siempre.
            fecha_ultima_activacion: esTraslado ? undefined : (estadoNavegacion ? dayjs().toISOString():undefined),
            // fecha_hora_inicio_instalacion: data.fecha_hora_inicio_instalacion ? dayjs().format("HH:mm:ss"):undefined,
            // TRASLADO: la hora fin la pone el TÉCNICO al finalizar (tras
            // materiales y fotos de salida). Si Activaciones la escribe aquí,
            // el tab de fotos cree que el traslado ya terminó y bloquea la
            // carga de las fotos de proceso. Instalaciones: igual que siempre.
            fecha_hora_fin_instalacion: esTraslado ? undefined : (estadoNavegacion ?
            dayjs().toISOString():undefined),
            distancia: data?.distancia,
            coordenadas: data?.coordenadas,

            router: data?.router,
            es_router_ac: data?.actualizar_nodo_olt ? data?.es_router_ac : false,
            requiere_wifi_6: data?.actualizar_nodo_olt
              ? data?.requiere_wifi_6
              : false,
            numero_cuotas_router_ac: data?.actualizar_nodo_olt
              ? data?.numero_cuotas_router_ac
              : 0,
            numero_cuotas_wifi_6: data?.actualizar_nodo_olt
              ? data?.numero_cuotas_wifi_6
              : 0,
            numero_cantidad_router_ac: data?.actualizar_nodo_olt
              ? data?.numero_cantidad_router_ac
              : 0,
            numero_cantidad_wifi_6: data?.actualizar_nodo_olt
              ? data?.numero_cantidad_wifi_6
              : 0,
            // "TRASLADO" es un centinela SOLO del front (no existe como fila de
            // api_instalacion y el campo es FK por codigo): se guarda null. Las
            // instalaciones nunca tienen ese valor, siguen mandando el suyo.
            tipo_instalacion: data?.tipo_instalacion === "TRASLADO" ? null : data?.tipo_instalacion,
            subtipo_instalacion: data?.subtipo_instalacion,
            

            equipos_utilizados: form.getValues()?.ont_a_utilizar?[form.getValues().ont_a_utilizar]:undefined,//proccesedEquiposUtilizados as MaterialTransferido[],
            materiales_equipos_adicionales: productos,

            puntos_red_instalados: data?.puntos_red_instalados||"0",
            puntos_red_no_instalados: data?.puntos_red_no_instalados||"0",
            routers_adicionales_instalados: data?.routers_adicionales_instalados||0,

            // forma_pago_adicionales_tecnico: data?.forma_pago_adicionales_tecnico||null,
          },
        });
      }else{
        await setActivationDataNoReturn.mutateAsync({
          id: ordenTrabajo?.id_orden_trabajo!,
          data: {
            actualizar_nodo_olt: data?.actualizar_nodo_olt,
            ipv4_actual: data?.ipv4_actual,
            // ipv6_actual: data.ipv6_actual!,
            pppoe: data?.pppoe,
            pppassword: data?.pppassword,

            // ----
            nodo: data?.nodo,
            olt: data?.olt,
            nap: data?.nap,
            puerto: data?.puerto,
            // serie_ont: selectedSerie,
            observaciones: {
              ...ordenTrabajo?.observaciones,
              observacion_activacion: data?.observacion_activacion ||"",
            },

            estado_activacion: undefined,
            // TRASLADO: esta fecha es el semáforo (verde solo si la activación
            // TERMINÓ bien) y la escribe el propio flujo de activación; el borrador
            // no la toca. En instalaciones se mantiene el comportamiento de siempre.
            fecha_ultima_activacion: esTraslado ? undefined : (estadoNavegacion ? dayjs().toISOString():undefined),
            // fecha_hora_inicio_instalacion: data.fecha_hora_inicio_instalacion ? dayjs().format("HH:mm:ss"):undefined,
            // TRASLADO: la hora fin la pone el TÉCNICO al finalizar (tras
            // materiales y fotos de salida). Si Activaciones la escribe aquí,
            // el tab de fotos cree que el traslado ya terminó y bloquea la
            // carga de las fotos de proceso. Instalaciones: igual que siempre.
            fecha_hora_fin_instalacion: esTraslado ? undefined : (estadoNavegacion ?
            dayjs().toISOString():undefined),
            distancia: data?.distancia,
            coordenadas: data?.coordenadas,

            router: data?.router,
            es_router_ac: data?.actualizar_nodo_olt ? data?.es_router_ac : false,
            requiere_wifi_6: data?.actualizar_nodo_olt
              ? data?.requiere_wifi_6
              : false,
            numero_cuotas_router_ac: data?.actualizar_nodo_olt
              ? data?.numero_cuotas_router_ac
              : 0,
            numero_cuotas_wifi_6: data?.actualizar_nodo_olt
              ? data?.numero_cuotas_wifi_6
              : 0,
            numero_cantidad_router_ac: data?.actualizar_nodo_olt
              ? data?.numero_cantidad_router_ac
              : 0,
            numero_cantidad_wifi_6: data?.actualizar_nodo_olt
              ? data?.numero_cantidad_wifi_6
              : 0,
            // "TRASLADO" es un centinela SOLO del front (no existe como fila de
            // api_instalacion y el campo es FK por codigo): se guarda null. Las
            // instalaciones nunca tienen ese valor, siguen mandando el suyo.
            tipo_instalacion: data?.tipo_instalacion === "TRASLADO" ? null : data?.tipo_instalacion,
            subtipo_instalacion: data?.subtipo_instalacion,
            

            equipos_utilizados: form.getValues()?.ont_a_utilizar?[form.getValues().ont_a_utilizar]:undefined,//proccesedEquiposUtilizados as MaterialTransferido[],
            materiales_equipos_adicionales: productos,

            puntos_red_instalados: data?.puntos_red_instalados||"0",
            puntos_red_no_instalados: data?.puntos_red_no_instalados||"0",
            routers_adicionales_instalados: data?.routers_adicionales_instalados||0,

            // forma_pago_adicionales_tecnico: data?.forma_pago_adicionales_tecnico||null,


          },
        });
      }
      setCanChangeNap(false)
    } catch (error) {
      toast.error(`Error al guardar la activación. Error: ${error}`);
      console.error("Error al guardar la activación:", error);
    }


    
  };

  ///* effects ------------------------
  useEffect(() => {
    setProductosAdicionales([])
  }, [])

  useEffect(() => {
    if (!ordenTrabajo?.id_orden_trabajo) return;

    const { prospecto_data, preventa_data, agendamiento_data, ...rest } =
      ordenTrabajo;
    if(ordenTrabajo?.materiales_equipos_adicionales?.length>0){
      setProductosAdicionales(
        ordenTrabajo?.materiales_equipos_adicionales
        || []
      )
    }

    ///* reset form -------------
    form.reset({
      ...prospecto_data,
      // ...preventa_data,
      // ...agendamiento_data,
      ...rest,
      es_bras: ordenTrabajo?.olt_data?.es_bras || false,
      ssid: ordenTrabajo?.contrato_data?.ssid || "",
      password_wifi: ordenTrabajo?.contrato_data?.password_wifi || "",
      router_data: ordenTrabajo?.router_data,
      planLabel: ordenTrabajo?.plan_data?.nombre || "",
      flotaLabel: ordenTrabajo?.flota_data?.nombre || "",
      // TRASLADO: la OT no trae tipo_instalacion (es de la preventa de una
      // instalación) y este reset corre DESPUÉS del efecto de DatosAdicionales
      // que preselecciona TRASLADO, pisándolo a "". Con el valor vacío,
      // campos_completados nunca se cumple y elegir Router tira "Debe completar
      // todos los DATOS TÉCNICOS". Instalaciones siguen igual (esTraslado=false).
      tipo_instalacion: ordenTrabajo?.tipo_instalacion || preventa_data?.tipo_instalacion || (esTraslado ? "TRASLADO" : ""),
      subtipo_instalacion: ordenTrabajo?.subtipo_instalacion || "",
      valor_instalacion: preventa_data?.valor_instalacion || "",

      observacion_1: ordenTrabajo?.preventa_data?.observaciones?.observacion_1,
      observacion_2: ordenTrabajo?.preventa_data?.observaciones?.observacion_2,
      observacion_llamada:
        agendamiento_data?.observaciones?.observacion_llamada,

      condicion_residencia: preventa_data?.condicion_residencia || "",

      numero_documento: rest.contrato_data?.numero_referencia,

      velocidad_internet: preventa_data?.velocidad
        ? `${preventa_data?.velocidad} ${rest?.plan_data?.unidad}`
        : "",

      //
      ciudad: rest.ciudad_data?.nombre,
      zona: rest.zona_data?.nombre,
      sector: rest.sector_data?.nombre,
      nap: rest.nap,
      rawNap: rest?.nap_data,
      distancia: agendamiento_data?.distancia || "0",
      puerto: rest.puerto,

      fecha_hora_inicio_instalacion: rest?.fecha_hora_inicio_instalacion
        ? rest?.fecha_hora_inicio_instalacion
        : null,
      fecha_hora_fin_instalacion: rest?.fecha_hora_fin_instalacion ? dayjs().toISOString() : null,
      observacion_activacion: rest?.observaciones?.observacion_activacion,
      coordenadas: rest?.coordenadas,

      //* preventa data -------------------
      es_router_ac: preventa_data?.es_router_ac,
      numero_cuotas_router_ac: preventa_data?.numero_cuotas_router_ac,
      requiere_wifi_6: preventa_data?.requiere_wifi_6,
      numero_cuotas_wifi_6: preventa_data?.numero_cuotas_wifi_6,
      numero_cantidad_router_ac: preventa_data?.numero_cantidad_router_ac,
      numero_cantidad_wifi_6: preventa_data?.numero_cantidad_wifi_6,
      ont_a_utilizar: ordenTrabajo?.equipos_utilizados?.[0] || null,
      estado_navegacion: ordenTrabajo?.contrato_data?.estado_navegacion || "",
      puntos_red_instalados: ordenTrabajo?.puntos_red_instalados || "0",
      puntos_red_no_instalados: ordenTrabajo?.puntos_red_no_instalados || "0",
      routers_adicionales_instalados: ordenTrabajo?.routers_adicionales_instalados || 0,

      puertoEnNap: ordenTrabajo?.nap_data?.puertos?.find(x=>x?.puerto==ordenTrabajo?.puerto)||null,

      equipos_adicionales_preventa: ordenTrabajo?.preventa_data?.equipos_adicionales || null



      // ...(ordenTrabajo?.forma_pago_adicionales_tecnico 
      //   && Object.keys(ordenTrabajo?.forma_pago_adicionales_tecnico).length>0 
      //   && {forma_pago_aux: form.getValues()?.forma_pago_adicionales_tecnico?.forma_pago,
      //       metodo_pago_mensualidad_aux: form.getValues()?.forma_pago_adicionales_tecnico?.metodo_pago_mensualidad,
      //       valor_instalacion_total_aux: form.getValues()?.forma_pago_adicionales_tecnico?.valor_instalacion_total,
      //       valor_instalacion_aux: form.getValues()?.forma_pago_adicionales_tecnico?.valor_instalacion,
      //       valor_instalacion_iva_aux: form.getValues()?.forma_pago_adicionales_tecnico?.valor_instalacion_iva,
      //       numero_cuenta_aux: form.getValues()?.forma_pago_adicionales_tecnico?.numero_cuenta,
      //       tipo_cuenta_bancaria_aux: form.getValues()?.forma_pago_adicionales_tecnico?.tipo_cuenta_bancaria,
      //       numero_tarjeta_aux: form.getValues()?.forma_pago_adicionales_tecnico?.numero_tarjeta,
      //       entidad_financiera_aux: form.getValues()?.forma_pago_adicionales_tecnico?.entidad_financiera,
      //       codigo_forma_pago_aux: form.getValues()?.forma_pago_adicionales_tecnico?.codigo_forma_pago,
      //       tipo_contrato_aux: form.getValues()?.forma_pago_adicionales_tecnico?.tipo_contrato,
      //       tarjeta_aux: form.getValues()?.forma_pago_adicionales_tecnico?.tarjeta,
      //     }
      //   ),


    } as unknown as any);
    // if( 
    //   ordenTrabajo?.materiales_equipos_adicionales?.length>0
    //   || ((ordenTrabajo?.preventa_data?.equipos_adicionales?.length||0)>0)
    // ){
    //   setProductosAdicionales(
    //     ordenTrabajo?.materiales_equipos_adicionales
    //     ||ordenTrabajo?.preventa_data?.equipos_adicionales
    //     ||[]
    //   )
    // }

    ///* set equipos disponibles -------------
  }, [ordenTrabajo]);


    ///* effects ------------------------
  useEffect(() => {
    const auxONTOptions = dataUbicacionProd?.data?.data?.map((item)=>{
      const auxONT = item?.series
      const auxONTData: any[] = []
      auxONT?.forEach((ont)=>{
        const auxONTSplit = ont.split('-')
        auxONTData.push({
          name: `${item.producto_data?.descripcion} - ${auxONTSplit[auxONTSplit.length-1]}`,
          codigo:item.producto_data?.codigo,
          cantidad:"1",
          series:[ont],
        })
      })
      return auxONTData
    })
    setOptionsONT(auxONTOptions?.flat() || []);


    // if (isResetForm) return;
    if (
      !ordenTrabajo?.id_orden_trabajo ||
      isLoadingNapsByCity
      // isRefetchingNapsByCity ||
      // isLoadingNodosByZona
      // isRefetchingNodosByZona
    )
      return;
  }, [
    optionsONT,
  ]);

  // clean up
  // useEffect(() => {
  //   return () => {
  //     handleClearAllProductos()
  //     clearAllMaterials();
  //   };
  // }, []);

  return (

    

    <SingleFormBoxScene
      titlePage={titlePage!}
      // titlePageNode={titlePageNode}
      onCancel={() => {
        navigate(customReturnUrl);
      }}
      saveTextBtn={"Guardar y Finalizar Activación"}
      disableSubmitBtn={disableSubmitBtn}
      showActionBtns={ordenTrabajo?.estado_activacion === EstadosActivacionInstallOTEnumChoice.GESTIONADA ?
        false : true}
      showCustomBtns={true}
      customBtns={
        <>
        {
          ordenTrabajo?.estado_activacion === EstadosActivacionInstallOTEnumChoice.GESTIONADA ?
          <Button variant="text" color="primary" onClick={() => navigate(customReturnUrl)}>
            Regresar
          </Button>
          :
          <Grid {...gridSizeMdLg12} container item justifyContent={"right"} spacing={2}>
            <Grid {...gridSizeMdLg12} container item justifyContent={"right"}>
              <Button variant="outlined" color="primary" onClick={async() =>{
                form.setValue("es_borrador",true)
                await onSave(form.getValues(), false)
                queryClient.invalidateQueries({ queryKey: [OrdenTrabajoQueryKeysEnum.OrdenTrabajo, ordenTrabajo?.id_orden_trabajo?.toString()] });
              }}>
                Guardar Borrador
              </Button>
            </Grid>
            {
              hasPermission(PermissionsEnum.administration_manage_system) && (
                <Grid {...gridSizeMdLg12} container item justifyContent={"right"}>
                  <Button variant="outlined" color="primary" onClick={async() =>{
                    await releaseLock()?.then(()=>toast.success("Orden de Trabajo de técnico liberada. El técnico puede continuar con sus instalaciones."));
                  }}>
                    Liberar OT Técnico
                  </Button>
                </Grid>
              )
            }
          </Grid>
        }
        </>
      }
      onSave={form.handleSubmit(()=>onSave(form.getValues(), true), (err) => {
        console.log({err})
        setAlert(
          true,
          "Debe completar los campos requeridos.",
          AlertSeverityEnum.error
        );
      })}

    >
      <Grid {...gridSizeMdLg12} container spacing={2} paddingTop={5}>

        {
          form.getValues()?.estado_activacion !== EstadosActivacionInstallOTEnumChoice.GESTIONADA?
            form.getValues()?.fecha_hora_inicio_instalacion?
            <CustomAlertCard
            alertColor='info'
            alertTitle="Nota"
            alertMessage={`${texto.orden_trabajo_iniciada} Iniciada a las ${dayjs(form.getValues()?.fecha_hora_inicio_instalacion).format('HH:mm:ss DD/MM/YYYY')}`}
            bgColorAlert="GREEN" />
            :
            <CustomAlertCard
            alertColor='info'
            alertTitle="Nota"
            alertMessage={texto.orden_trabajo_no_iniciada}
            bgColorAlert="YELLOW" />
          :null
        }

        <Grid {...gridSizeMdLg12} container item spacing={2} mb={3}>
          <Grid {...gridSizeMdLg12} item spacing={2} py={2}>
            <Typography variant="h6">DATOS DEL CLIENTE</Typography>
          </Grid>
          <ClientesFormComponentActivaciones
            form={form}
            soloLecturaMapa={esTraslado
              && ordenTrabajo?.estado_activacion === EstadosActivacionInstallOTEnumChoice.GESTIONADA}
          />
        </Grid>


        <Grid {...gridSizeMdLg12} container item spacing={2} mb={3}>
          <Grid {...gridSizeMdLg12} item spacing={2} py={2}>
            <Typography variant="h6">DATOS TÉCNICOS</Typography>
          </Grid>
          {
            !form.getValues()?.fecha_hora_inicio_instalacion?
            <CustomAlertCard
              alertColor='info'
              alertTitle="Nota"
              alertMessage={texto.orden_trabajo_no_iniciada}
              bgColorAlert="YELLOW" />
            :
            <DatosTecnicosFormComponentActivaciones
              form={form}
              ordenTrabajo={ordenTrabajo}
              napsByCity={napsByCity?.data?.data}
              isLoadingNapsByCity={isLoadingNapsByCity}
              optionsONT={[...ontsDelCliente, ...optionsONT]}
              isloadingUbProducto={isloadingUbProducto}
              guardarBorrador={onSave}
              canChangeNap={canChangeNap}
              setCanChangeNap={setCanChangeNap}
              permitirCambioNapActivo={esTraslado}
              permitirOntDelCliente={permitirOntDelCliente}
              onOntClienteSeleccionada={onOntClienteSeleccionada}
              usarOntCliente={usarOntCliente}
              onCambiarUsarOntCliente={onCambiarUsarOntCliente}
              dialogoOntClienteAbierto={dialogoOntClienteAbierto}
              onCerrarDialogoOntCliente={onCerrarDialogoOntCliente}
              cobroOnt={cobroOnt}
              onCambiarCobroOnt={setCobroOnt}
            />
          }
        </Grid>


        <Grid {...gridSizeMdLg12} container item spacing={4}>
          <Grid {...gridSizeMdLg12} item spacing={2}>
            <Typography variant="h6">DATOS ADICIONALES</Typography>  
          </Grid>
          <DatosAdicionalesFormComponentActivaciones form={form} ordenTrabajo={ordenTrabajo} />
        </Grid>

        {/* Secciones extra (solo traslados): integradas en el mismo orden del formulario. */}
        {seccionesAdicionales && (
          <Grid {...gridSizeMdLg12} container item spacing={2} mt={1}>
            {seccionesAdicionales}
          </Grid>
        )}

        <Grid {...gridSizeMdLg12} item spacing={2} my={4}
        sx={{ position:"sticky", bottom:0, backgroundColor:"#fff", zIndex:1}}>
          <SummaryPanelComponentActivaciones
            color="primary"
            hex="#f0f"
            form={form}
            fieldsToCheck={esBrasValue? 
              ["tipo_instalacion","ont_a_utilizar","puerto","nap"]
              :["tipo_instalacion","ont_a_utilizar","puerto","router","nap"]}
          />
        </Grid>
      </Grid>
    </SingleFormBoxScene>
  );
};

export default SaveActivacionInstallPendingOT;
